import { useMemo } from 'react'
import { motion } from 'framer-motion'

/**
 * MonthCalendar — 7-column grid for a single month, tinted by activity density.
 * Rendered inside a bottom sheet triggered by the calendar icon on Timeline.
 *
 * Cells are square. Each cell shows:
 *   - Day number (top-left)
 *   - Intensity tint (background) — pale coral for low, deep coral for high
 *   - Today gets an outlined ring
 *   - Selected day gets a mint ring
 */

interface Props {
  /** Year + month to render, e.g. { year: 2026, month: 8 } for September 2026 (month is 0-indexed) */
  year: number
  month: number
  /** Map of ISO date -> event count for that day */
  activityByDay: Map<string, number>
  /** ISO date currently selected, or null */
  selectedDate: string | null
  /** Called when a day cell is tapped */
  onSelect: (date: string) => void
  /** Called when user taps the < or > chevrons to change month */
  onChangeMonth?: (delta: -1 | 1) => void
}

export function MonthCalendar({ year, month, activityByDay, selectedDate, onSelect, onChangeMonth }: Props) {
  const cells = useMemo(() => buildMonth(year, month), [year, month])
  const todayIso = toIsoDate(new Date())
  const maxCount = useMemo(() => {
    let max = 0
    for (const [iso, c] of activityByDay) {
      if (iso.startsWith(`${year}-${String(month + 1).padStart(2, '0')}`) && c > max) max = c
    }
    return max || 1
  }, [activityByDay, year, month])

  const monthLabel = new Date(year, month, 1).toLocaleDateString([], { month: 'long', year: 'numeric' })

  return (
    <div className="p-2">
      <div className="flex items-center justify-between px-2 pb-3">
        {onChangeMonth ? (
          <button
            type="button"
            onClick={() => onChangeMonth(-1)}
            className="icon-btn h-9 w-9"
            aria-label="Previous month"
          >
            ‹
          </button>
        ) : <span className="w-9" />}
        <div className="font-display text-title font-medium">{monthLabel}</div>
        {onChangeMonth ? (
          <button
            type="button"
            onClick={() => onChangeMonth(1)}
            className="icon-btn h-9 w-9"
            aria-label="Next month"
          >
            ›
          </button>
        ) : <span className="w-9" />}
      </div>

      <div className="grid grid-cols-7 gap-1 px-1 pb-1">
        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
          <div key={i} className="text-tag text-center text-text-tertiary py-1">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1 px-1 pb-2">
        {cells.map((cell, i) => {
          if (!cell) return <div key={i} className="aspect-square" />
          const { iso, day } = cell
          const count = activityByDay.get(iso) ?? 0
          const intensity = Math.min(1, count / maxCount)
          const isToday = iso === todayIso
          const isSelected = iso === selectedDate
          return (
            <motion.button
              key={iso}
              whileTap={{ scale: 0.92 }}
              type="button"
              onClick={() => onSelect(iso)}
              className="relative aspect-square rounded-r-sm flex flex-col items-center justify-center transition-all"
              style={{
                background:
                  isSelected
                    ? 'linear-gradient(180deg, #40DDAF 0%, #2ED2A3 55%, #22B98D 100%)'
                    : count > 0
                      ? `rgba(255, 122, 45, ${0.08 + intensity * 0.42})`
                      : 'rgb(var(--bg-elevated) / 0.5)',
                boxShadow: isToday && !isSelected ? 'inset 0 0 0 2px #FF7A2D' : undefined,
                color: isSelected ? 'white' : undefined,
              }}
              aria-label={`${day} ${new Date(iso).toLocaleDateString([], { month: 'long' })}${count ? `, ${count} events` : ''}`}
              aria-pressed={isSelected}
            >
              <span className="text-caption font-display font-medium leading-none">{day}</span>
              {count > 0 && !isSelected && (
                <span className="text-tag opacity-70 mt-0.5 leading-none">{count}</span>
              )}
            </motion.button>
          )
        })}
      </div>

      <div className="px-3 pt-3 pb-2 flex items-center gap-2 text-tag text-text-tertiary">
        <span>Less</span>
        <span className="h-3 w-6 rounded-sm" style={{ background: 'rgb(var(--bg-elevated) / 0.5)' }} />
        <span className="h-3 w-6 rounded-sm" style={{ background: 'rgba(255,122,45,0.12)' }} />
        <span className="h-3 w-6 rounded-sm" style={{ background: 'rgba(255,122,45,0.28)' }} />
        <span className="h-3 w-6 rounded-sm" style={{ background: 'rgba(255,122,45,0.45)' }} />
        <span>More</span>
      </div>
    </div>
  )
}

function buildMonth(year: number, month: number): Array<null | { iso: string; day: number }> {
  const firstDay = new Date(year, month, 1)
  const startWeekday = firstDay.getDay() // Sun=0..Sat=6
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const cells: Array<null | { iso: string; day: number }> = []
  for (let i = 0; i < startWeekday; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`
    cells.push({ iso, day: d })
  }
  // Pad to full weeks (multiple of 7) so grid is clean
  while (cells.length % 7 !== 0) cells.push(null)
  return cells
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${dd}`
}
